
// client/src/components/media/ScheduledPostsList.jsx
// Read-only + cancel management UI for upcoming scheduled social posts.
//
// Uses your existing /api/schedule/upcoming/:tenantId and DELETE /api/schedule/:postId endpoints,
// and the worker stack still handles actually publishing posts.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  fetchUpcomingScheduledPosts,
  cancelScheduledPost,
} from "../../services/scheduledPostsApi";
// import MediaGrid from "./MediaGrid"; // Temporarily disabled
import AppFooter from "../AppFooter";
import {
  FaArrowLeft,
  FaCalendarPlus,
  FaTrash,
  FaEye,
  FaList,
  FaHome,
} from "react-icons/fa";
import "./ScheduledPostsList.css";
import SearchFilterBar from "../shared/SearchFilterBar";
import ContentApprovalPanel from "../campaign/ContentApprovalPanel";
import { useTranslation } from "react-i18next";
import { getJson } from "../../services/http";
import { formatCount, formatMoney } from "../analytics/attributionFormat";
import { RefreshButton } from "../common/IconButton";
import "../analytics/PerformancePanel.css";

/**
 * Helper to safely format dates coming back from MySQL/Node.
 * Always displays in the tenant's configured timezone, not the browser's local time.
 */
function formatDate(value, tz = 'UTC') {
  if (!value) return "—";
  try {
    const d = parseDateMaybeMySQL(value);
    if (!d) return String(value);
    return d.toLocaleString('en-US', { timeZone: tz });
  } catch {
    return String(value);
  }
}

/**
 * Helper to format date in short format for embedded display.
 * Shows date and time in a compact format in the tenant's timezone.
 */
function formatDateShort(value, tz = 'UTC') {
  if (!value) return "—";
  try {
    const d = parseDateMaybeMySQL(value);
    if (!d) return String(value);
    // Format as "Jan 11, 3:45 PM" in the tenant's timezone
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: tz,
    }) + ', ' + d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: tz,
    });
  } catch {
    return String(value);
  }
}

/**
 * Parse MySQL-ish datetime safely.
 * Handles:
 *  - Date objects
 *  - ISO strings
 *  - "YYYY-MM-DD HH:mm:ss" (common MySQL DATETIME)
 */
function parseDateMaybeMySQL(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value);

  const normalized =
    /\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(raw) && !raw.includes("T")
      ? raw.replace(" ", "T") + "Z"
      : raw;

  const d = new Date(normalized);
  if (!Number.isNaN(d.getTime())) return d;

  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

/**
 * Compare at "date" granularity (local day start) to avoid time-of-day quirks.
 */
function toLocalDayStart(dateLike) {
  const d = parseDateMaybeMySQL(dateLike);
  if (!d) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * A post can be cancelled while it's in the queue / being picked up,
 * but not after it's already been posted.
 */
function isCancellable(post) {
  const status = String(post?.status || "").toLowerCase();
  return status === "queued" || status === "posting";
}

/**
 * Truncate text to a maximum length, adding ellipsis if needed
 */
function truncateText(text, maxLength) {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "…";
}

/**
 * Same extension check SchedulePost uses to sort a flat media_urls array
 * into images vs. videos. Kept in sync so a post's media survives the
 * handoff intact instead of silently losing anything that isn't an image.
 */
function isImageUrl(u = "") {
  const clean = String(u).split("?")[0];
  const ext = clean.includes(".") ? clean.split(".").pop().toLowerCase() : "";
  return ["jpg", "jpeg", "png", "gif", "webp", "heic", "avif"].includes(ext);
}

// NOTE: we add `embedded = false` so the component can render inside the Dashboard
// without its own footer, but all existing standalone behavior is unchanged.
export default function ScheduledPostsList({
  tenantId: tenantIdProp,
  embedded = false,
}) {
  const { tenant: tenantFromRoute } = useParams();
  const navigate = useNavigate();

  // Resolve numeric tenantId – same pattern as SchedulePost:
  // Number(:tenant) || 1 as a fallback.
  const tenantId = useMemo(
    () => Number(tenantIdProp ?? tenantFromRoute) || 1,
    [tenantIdProp, tenantFromRoute]
  );

  const [tenantTimezone, setTenantTimezone] = useState('UTC');
  const [companyName, setCompanyName] = useState("");
  const { t } = useTranslation();
  // Growth Phase 1: tracked-link clicks / conversions / revenue per posted post.
  const [attr, setAttr] = useState({});
  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    // NOTE: embedded view *displays* 3, but we may fetch more to keep the list filled after filtering.
    limit: embedded ? 25 : 50,
    offset: 0,
    hasMore: false,
  });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [cancelingId, setCancelingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Choose a wrapper class:
  // - Standalone: Bootstrap container
  // - Embedded: slim wrapper that fits nicely in Dashboard card cell
  const wrapperClass = embedded
    ? "dashboard-scheduled-container"
    : "container py-3";

  /**
   * Cutoff = START OF TODAY at local day granularity.
   *
   * Requirement:
   *  - Show posts scheduled for today and future dates
   *  - Hide posts scheduled in the past (before today)
   *
   * Scheduled posts schema context:
   * - t_social_post.scheduled_on is when the post should be published
   */
  const cutoffDay = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  /**
   * Core loader – can load the first page or append "load more" pages.
   */
  const loadPage = useCallback(
    async ({ append = false, offsetOverride } = {}) => {
      if (!tenantId) return;

      // Dashboard (embedded) should DISPLAY 3, but we FETCH more to survive filtering.
      const limit = embedded ? 25 : 50;

      const offset =
        offsetOverride ?? (append ? pagination.offset + limit : 0);

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError("");

      try {
        const data = await fetchUpcomingScheduledPosts(tenantId, {
          limit,
          offset,
          all: !embedded,
        });

        const nextPosts = Array.isArray(data?.posts) ? data.posts : [];

        const nextPagination = data?.pagination || {
          total: nextPosts.length,
          limit,
          offset,
          hasMore: false,
        };

        setPosts((prev) => (append ? [...prev, ...nextPosts] : nextPosts));
        setPagination(nextPagination);
      } catch (err) {
        console.error("Failed to load scheduled posts", err);
        setError(err?.message || "Failed to load scheduled posts.");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [tenantId, pagination.limit, pagination.offset, embedded]
  );

  // Per-post attribution for posted posts (one request per page of posts).
  useEffect(() => {
    if (!tenantId || embedded) return undefined;
    const ids = posts
      .filter((p) => String(p.status || "").toLowerCase() === "posted")
      .map((p) => p.id)
      .filter((id) => !(id in attr));
    if (!ids.length) return undefined;
    let cancelled = false;
    getJson(`/api/analytics/${Number(tenantId)}/attribution/posts?ids=${ids.slice(0, 200).join(",")}`)
      .then((res) => {
        if (cancelled) return;
        const got = res?.data?.posts || {};
        setAttr((prev) => {
          const next = { ...prev };
          for (const id of ids) next[id] = got[id] || { clicks: 0, conversions: 0, revenue: 0 };
          return next;
        });
      })
      .catch(() => { /* metrics are optional; the list still renders */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, embedded, posts]);

  // Initial load + reload when tenant changes
  useEffect(() => {
    loadPage({ append: false, offsetOverride: 0 });
  }, [tenantId]);

  // Fetch company name and timezone for display
  const baseUrl = process.env.REACT_APP_BASE_URL || '';
  useEffect(() => {
    if (!tenantId) return;
    axios.get(`${baseUrl}/api/tenants/${tenantId}`)
      .then(res => {
        if (res.data?.tenant?.companyname) {
          setCompanyName(res.data.tenant.companyname);
        }
      })
      .catch(() => {});
    axios.get(`${baseUrl}/api/tenants/${tenantId}/timezone`)
      .then(res => {
        if (res.data?.timezone) setTenantTimezone(res.data.timezone);
      })
      .catch(() => {});
  }, [tenantId, baseUrl]);

  /**
   * DASHBOARD FILTERING (embedded mode only)
   * - Uses status + scheduled_on to hide "not active" items.
   *
   * Schema:
   * - t_social_post.status: queued|posting|posted|failed|cancelled
   * - t_social_post.scheduled_on: when to publish
   */
  const searchFilteredPosts = useMemo(() => {
    if (embedded) return posts;
    let result = posts;
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      result = result.filter((p) => {
        const content = (p.content || "").toLowerCase();
        const platforms = (Array.isArray(p.platforms) ? p.platforms.join(" ") : "").toLowerCase();
        const status = (p.status || "").toLowerCase();
        return content.includes(q) || platforms.includes(q) || status.includes(q);
      });
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      result = result.filter((p) => {
        const d = parseDateMaybeMySQL(p.scheduled_on ?? p.scheduledOn ?? p.scheduledAt);
        return d ? d >= from : true;
      });
    }
    if (dateTo) {
      const to = new Date(dateTo + "T23:59:59");
      result = result.filter((p) => {
        const d = parseDateMaybeMySQL(p.scheduled_on ?? p.scheduledOn ?? p.scheduledAt);
        return d ? d <= to : true;
      });
    }
    return result;
  }, [posts, embedded, searchTerm, dateFrom, dateTo]);

  const visiblePosts = useMemo(() => {
    if (!embedded) return searchFilteredPosts;

    const allowedStatuses = new Set(["queued", "posting"]);

    const filtered = (posts || []).filter((p) => {
      if (!p) return false;

      const statusLower = String(p.status || "").toLowerCase().trim();

      // "Active" scheduled posts on the Dashboard = queued/posting only
      if (statusLower && !allowedStatuses.has(statusLower)) return false;

      // Scheduled time is the effective date for visibility
      const scheduledCandidate =
        p.scheduled_on ?? p.scheduledOn ?? p.scheduledAt ?? null;

      // If there's no scheduled date, it's an immediate post - show it!
      if (!scheduledCandidate) return true;
      
      const scheduledDay = toLocalDayStart(scheduledCandidate);
      if (!scheduledDay) return true; // If we can't parse the date, show it

      // Show posts scheduled for today or future (>= today)
      return scheduledDay >= cutoffDay;
    });

    // Dashboard should show only 3 items after filtering
    return filtered.slice(0, 3);
  }, [posts, embedded, cutoffDay, searchFilteredPosts]);

  /**
   * Navigate to SchedulePost with this scheduled post as defaults.
   * We pass everything through location.state so SchedulePost can pre-fill.
   *
   * When embedded in the Dashboard, we prefer the event-aware route:
   *   /:tenant/:eventId/:eventItemId/schedulePost
   * When standalone, we use the simpler:
   *   /:tenant/media/schedule
   */
  const handleOpen = (post) => {
    if (!post) return;

    const {
      id: postId,
      eventId,
      eventid,
      eventItemId,
      eventitemid,
      content,
      media_urls,
      platforms,
      scheduled_on,
      status,
    } = post;

    // The user might click a "posted" item in standalone mode to view results.
    // We'll still navigate to SchedulePost so they can see the payload.
    const finalEventId = eventId || eventid;
    const finalItemId = eventItemId || eventitemid;

    // Split the flat media_urls array into images/videos, same as every other
    // entry point into SchedulePost (EventDetail, EventItemDetail, QuickPost).
    // SchedulePost only reads videos from state.videos — passing the merged
    // mediaUrls alone silently dropped any video when reopening this post.
    const mediaList = Array.isArray(media_urls) ? media_urls : [];
    const mediaImages = mediaList.filter(isImageUrl);
    const mediaVideos = mediaList.filter((u) => !isImageUrl(u));

    // Build a state object that SchedulePost can read
    const stateForSchedule = {
      postId,
      // Include event references if present
      ...(finalEventId && { eventId: finalEventId }),
      ...(finalItemId && { eventItemId: finalItemId }),
      content,
      images: mediaImages,
      videos: mediaVideos,
      mediaUrls: media_urls,
      platforms: Array.isArray(platforms) ? platforms : [],
      scheduledOn: scheduled_on,
      status,
    };

    let path = "";
    if (embedded && finalEventId && finalItemId) {
      // Event-item route
      path = `/${tenantFromRoute || tenantId}/${finalEventId}/${finalItemId}/schedulePost`;
    } else if (embedded && finalEventId) {
      // Event-only route
      path = `/${tenantFromRoute || tenantId}/${finalEventId}/schedulePost`;
    } else {
      // Generic route (no event context or standalone mode)
      path = `/${tenantFromRoute || tenantId}/media/schedule`;
    }

    navigate(path, { state: stateForSchedule });
  };

  /**
   * Cancel a scheduled post – prompt + backend call
   */
  const handleCancel = async (post) => {
    if (!post?.id) return;

    const confirm = window.confirm(
      `Cancel scheduled post?
This will remove it from the queue and prevent it from posting.`
    );
    if (!confirm) return;

    setCancelingId(post.id);

    try {
      await cancelScheduledPost(post.id, tenantId);

      // Remove from local state
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
    } catch (err) {
      console.error("Failed to cancel post", err);
      alert(err?.message || "Failed to cancel post.");
    } finally {
      setCancelingId(null);
    }
  };

  /**
   * Refresh the list by reloading from the beginning
   */
  const onRefresh = useCallback(() => {
    loadPage({ append: false, offsetOverride: 0 });
  }, [loadPage]);

  /**
   * Load the next page of results (pagination)
   */
  const onLoadMore = useCallback(() => {
    loadPage({ append: true });
  }, [loadPage]);

  /**
   * Navigate to standalone ScheduledPostsList to view all posts
   * (used in embedded mode)
   */
  const handleViewAll = () => {
    navigate(`/${tenantFromRoute || tenantId}/media/scheduled-posts`);
  };

  const hasVisiblePosts = visiblePosts.length > 0;

  return (
    <div className={`${wrapperClass} scheduled-posts-theme`}>
      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <div>
          <h5 className="mb-0">Scheduled Social Posts</h5>
          {!embedded && companyName && <small className="text-gray-400">{companyName}</small>}
        </div>

        {/* Optional refresh button - only in standalone mode */}
        {!embedded && (
          <RefreshButton onClick={onRefresh} loading={loading} />
        )}
      </div>

      {/* Desktop action bar - standalone mode only. Mirrors the AppFooter
          buttons; on desktop this replaces the bottom footer (which desktop.css
          hides whenever a .desktop-action-bar is present) for the same top-bar
          layout the rest of the app uses. */}
      {!embedded && (
        <div className="desktop-action-bar">
          <button type="button" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back
          </button>
          <button type="button" onClick={() => navigate(`/${tenantId}/events`)}>
            <FaList /> Event List
          </button>
          <button type="button" onClick={() => navigate(`/${tenantFromRoute || tenantId}/dashboard`)}>
            <FaHome /> Home
          </button>
          <button
            type="button"
            className="btn-primary-action"
            onClick={() => navigate(`/${tenantFromRoute || tenantId}/media/schedule`)}
          >
            <FaCalendarPlus /> New schedule
          </button>
        </div>
      )}

      {/* Search + date filter - standalone mode only */}
      {!embedded && (
        <SearchFilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onClear={() => { setSearchTerm(""); setDateFrom(""); setDateTo(""); }}
          searchPlaceholder={"Search scheduled posts\u2026"}
        />
      )}

      {/* Error banner */}
      {!!error && (
        <div className="alert alert-danger" role="alert">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Loading state */}
      {loading && !hasVisiblePosts && (
        <div className="text-center text-gray-400 py-4">
          Loading scheduled posts…
        </div>
      )}

      {/* Empty state */}
      {!loading && !hasVisiblePosts && !error && (
        <div className="text-center text-gray-400 py-4">
          {embedded 
            ? "No active scheduled posts." 
            : "No scheduled posts found for this tenant."}
        </div>
      )}

      {/* Scheduled posts list */}
      {hasVisiblePosts && (
        <div className="list-group mb-3">
          {visiblePosts.map((post) => {
            // Format platforms for display
            const platforms = Array.isArray(post.platforms)
              ? post.platforms.join(", ")
              : "";
            
            // Format scheduled time in the tenant's timezone
            const scheduledFor = embedded
              ? formatDateShort(post.scheduled_on || post.created_on, tenantTimezone)
              : formatDate(post.scheduled_on || post.created_on, tenantTimezone);
            
            // Get status for styling
            const status = String(post.status || "").toLowerCase();

            // Get post content preview. Standalone cards now share space with a
            // thumbnail, so the snippet is kept short (~90 chars) rather than
            // the old 200-char block of text.
            const contentPreview = embedded
              ? truncateText(post.content, 25)
              : truncateText(post.content, 90);

            // Cover thumbnail: first item in media_urls (videos are queued
            // ahead of images when a post is scheduled, so this naturally
            // picks the video when one is present, same as MediaPost/PostsList).
            const coverUrl = Array.isArray(post.media_urls) && post.media_urls.length
              ? post.media_urls[0]
              : null;
            const coverIsVideo = coverUrl ? !isImageUrl(coverUrl) : false;

            // Standalone platform list is trimmed to a couple of badges + "+N"
            // instead of a long comma-separated line.
            const platformList = platforms ? platforms.split(",").map((p) => p.trim()).filter(Boolean) : [];
            const shownPlatforms = platformList.slice(0, 2);
            const extraPlatformCount = platformList.length - shownPlatforms.length;

            // Try to resolve event / item names but fall back to IDs
            const eventName =
              post.eventName ||
              post.eventname ||
              post.event_name ||
              (post.event && (post.event.name || post.event.title)) ||
              (post.eventid ? `Event #${post.eventid}` : "No event");

            const itemName =
              post.itemName ||
              post.itemname ||
              post.eventitemname ||
              post.event_item_name ||
              (post.item && (post.item.name || post.item.title)) ||
              (post.eventitemid ? `Item #${post.eventitemid}` : "");

            return (
              <div
                key={post.id}
                className="list-group-item mb-2 rounded shadow-sm"
                data-platform={platforms.split(',')[0]?.trim().toLowerCase()}
                data-status={status}
                style={{ cursor: "pointer" }}
                onClick={() => handleOpen(post)}
              >
                {/* EMBEDDED MODE: Compact layout matching ScheduledEventsList pattern */}
                {embedded && (
                  <>
                    {/* 
                      REQUIREMENT: Layout structure (matching ScheduledEventsList)
                      Row 1: Content preview (left) | Status badge (right)
                      Row 2: Scheduled date/time (left) | Platform badge (right)
                      NOTE: Left side is CLAMPED to 2 lines total.
                    */}
                    <div className="flex justify-between items-start">
                      {/* LEFT: exactly 2 lines (content + date), left aligned */}
                      <div className="flex-grow os-post-left">
                        {/* Line 1: Content preview */}
                        <strong className="os-post-title">
                          {contentPreview || "No content"}
                        </strong>

                        {/* Line 2: Scheduled date/time */}
                        <span className="os-post-date">
                          {scheduledFor}
                        </span>
                      </div>

                      {/* RIGHT: badges + cancel stacked vertically */}
                      <div className="flex flex-col items-end gap-1">
                        {/* Status badge on top (QUEUED, POSTING, etc.) */}
                        <span
                          className={
                            "badge text-uppercase " +
                            (status === "queued"
                              ? "bg-warning text-dark"
                              : status === "posting"
                              ? "bg-info"
                              : status === "posted"
                              ? "bg-success"
                              : status.startsWith("fail")
                              ? "bg-danger"
                              : "bg-secondary")
                          }
                        >
                          {status}
                        </span>

                        {/* Cancel button for cancellable posts */}
                        {isCancellable(post) && (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm py-0 px-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancel(post);
                            }}
                            disabled={cancelingId === post.id}
                          >
                            <FaTrash className="mr-1" style={{ fontSize: "0.7rem" }} />
                            <small>{cancelingId === post.id ? "Cancelling…" : "Cancel"}</small>
                          </button>
                        )}

                        {/* Platform badges below status (up to 2 + ellipsis) */}
                        {platforms && (() => {
                          const all = platforms.split(',').map(p => p.trim()).filter(Boolean);
                          const shown = all.slice(0, 2);
                          const hasMore = all.length > 2;
                          return (
                            <div className="os-platform-badges">
                              {shown.map((p) => (
                                <span
                                  key={p}
                                  className="badge bg-gray-600 uppercase"
                                  data-platform={p.trim().toLowerCase()}
                                >
                                  {p}
                                </span>
                              ))}
                              {hasMore && (
                                <span className="badge bg-gray-600">…</span>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </>
                )}

                {/* STANDALONE MODE: Full layout with all details */}
                {!embedded && (
                  <>
                    {/* Top row: date/time + status */}
                    <div className="flex justify-between mb-1">
                      <div className="flex-grow">
                        <small className="text-gray-400 block">{scheduledFor}</small>

                        {/* Event / item names (or fallback labels) - only show in standalone mode */}
                        <div className="small text-gray-400">
                          {eventName}
                          {itemName ? ` • ${itemName}` : ""}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-1">
                        {/* Status badge with color coding */}
                        <span
                          className={
                            "badge text-uppercase " +
                            (status === "queued"
                              ? "bg-primary"
                              : status === "posting"
                              ? "bg-info"
                              : status === "posted"
                              ? "bg-success"
                              : status.startsWith("fail")
                              ? "bg-danger"
                              : "bg-secondary")
                          }
                        >
                          {status}
                        </span>

                        {/* Cancel button - only for cancellable posts in standalone mode */}
                        {isCancellable(post) && (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={(e) => {
                              e.stopPropagation(); // prevent opening SchedulePost
                              handleCancel(post);
                            }}
                            disabled={cancelingId === post.id}
                          >
                            <FaTrash className="mr-1" />
                            {cancelingId === post.id ? "Cancelling…" : "Cancel"}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Content approval (On-Screen 2.0) — only when the post is
                        actually in a review flow, so the common "approved"
                        case stays out of the way. */}
                    {post.approval_status &&
                      post.approval_status !== "approved" &&
                      status !== "posted" && (
                        <div
                          className="mb-2"
                          onClick={(e) => e.stopPropagation()}
                          role="presentation"
                        >
                          <ContentApprovalPanel
                            tenantId={tenantId}
                            post={post}
                            compact
                            onChanged={() => loadPage({ append: false, offsetOverride: 0 })}
                          />
                        </div>
                      )}

                    {status === "posted" && attr[post.id] && (
                      <div className="sp-attr" data-testid={`post-attr-${post.id}`}>
                        <span>{t("performance.clicks", "Clicks")}: {formatCount(attr[post.id].clicks)}</span>
                        <span>{t("performance.conversions", "Conversions")}: {formatCount(attr[post.id].conversions)}</span>
                        <span>{t("performance.revenue", "Revenue")}: {formatMoney(attr[post.id].revenue)}</span>
                      </div>
                    )}

                    {/* Thumbnail (left) + platforms/content (right). The thumb's
                        height is capped to roughly match this text block, and
                        its width to at most half the card, via .os-post-thumb
                        in ScheduledPostsList.css. */}
                    <div className="flex os-post-body">
                      {coverUrl && (
                        <div className="os-post-thumb">
                          {coverIsVideo ? (
                            <video src={coverUrl} playsInline muted preload="metadata" />
                          ) : (
                            <img src={coverUrl} alt="" loading="lazy" />
                          )}
                        </div>
                      )}

                      <div className="flex-grow os-post-text">
                        {/* Platforms – trimmed to a couple of badges + "+N" */}
                        {shownPlatforms.length > 0 && (
                          <div className="os-platform-badges mb-1" style={{ justifyContent: "flex-start" }}>
                            {shownPlatforms.map((p) => (
                              <span
                                key={p}
                                className="badge bg-gray-600 uppercase"
                                data-platform={p.toLowerCase()}
                              >
                                {p}
                              </span>
                            ))}
                            {extraPlatformCount > 0 && (
                              <span className="badge bg-gray-600">+{extraPlatformCount}</span>
                            )}
                          </div>
                        )}

                        {/* Content snippet – short, since the thumbnail now shares the row */}
                        {contentPreview && (
                          <p className="mb-0 text-gray-400 small">
                            {contentPreview}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Failure reason – tells the user WHY, not just that it failed */}
                    {status.startsWith("fail") && post.error && (
                      <p className="mb-0 small text-danger">
                        <strong>Reason:</strong> {post.error}
                      </p>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* "View All" button - only shown in embedded mode when there are visible posts */}
      {embedded && hasVisiblePosts && (
        <div className="text-center mb-3">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleViewAll}
          >
            <FaEye className="mr-1" />
            VIEW ALL SCHEDULED POSTS
          </button>
        </div>
      )}

      {/* Pagination - only in standalone mode */}
      {!embedded && pagination.hasMore && (
        <div className="text-center mb-3">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}

      {/* Footer – keep existing behavior for standalone views.
          In embedded mode (Dashboard) we hide this so you don't get a nested footer. */}
      {!embedded && (
        <AppFooter
          buttons={[
            {
              icon: <FaArrowLeft />,
              label: "Back",
              onClick: () => navigate(-1),
            },
            {
              icon: <FaList />,
              label: "Event List",
              onClick: () => navigate(`/${tenantId}/events`),
            },
            {
              icon: <FaCalendarPlus />,
              label: "New schedule",
              onClick: () =>
                navigate(`/${tenantFromRoute || tenantId}/media/schedule`),
            },
          ]}
        />
      )}
    </div>
  );
}